import { MutationFunctionOptions } from '@apollo/react-common';
import { Confirmation, Content, Footer, Header, TripleAction } from '../../../libs/gs-component-right-rail';
import Icon from '@xo-union/tk-component-icons';
import Spinner from '@xo-union/tk-component-spinner';
import Honeybadger from 'honeybadger-js';
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useHistory, useLocation } from 'react-router-dom';
import { SortEnd } from 'react-sortable-hoc';

import {
  PhotoItemsFragment,
  UpdatePhotoGalleryItemMutation,
  UpdatePhotoGalleryItemMutationVariables,
  useDeletePhotoGalleryItemMutation,
  usePageQuery,
  useWeddingWebsiteQuery,
  Wws_PageItem,
} from '../../../generated/graphql';
import deletePageItem from '../../../helpers/apolloCache/deletePageItem';
import { reorder } from '../../../helpers/reorder';
import { previewPageRoute, previewRoute } from '../../../helpers/routes';
import { Remover } from '../../FormComponents';
import { pagesQuery } from '../../LeftContainer/graphql';
import styles from './edit.scss';
import PhotoGalleryGrid from './Grid';

interface PhotoGalleryEditProps {
  addUploads(fileRefs: File[]): Promise<void>;
  addUploadsOnChange(event: React.ChangeEvent<HTMLInputElement>): void;
  isGalleryLoading: boolean;
  id: number | null;
  isNew: boolean;
  pageId: number;
  photoItems: PhotoItemsFragment[];
  setPhotoItems(photoItems: PhotoItemsFragment[]): void;
  update(
    options: MutationFunctionOptions<UpdatePhotoGalleryItemMutation, UpdatePhotoGalleryItemMutationVariables>
  ): Promise<any>;
  trackItem(pageItem?: Wws_PageItem): void;
}
const PhotoGalleryEdit = (props: PhotoGalleryEditProps) => {
  const {
    addUploads,
    addUploadsOnChange,
    isGalleryLoading,
    id,
    isNew,
    pageId,
    photoItems,
    setPhotoItems,
    update,
    trackItem,
  } = props;
  const history = useHistory();
  const { pathname } = useLocation();
  const [destroy] = useDeletePhotoGalleryItemMutation();
  const { data: weddingData } = useWeddingWebsiteQuery();
  const { data: pageData } = usePageQuery({ variables: { id: pageId } });
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: addUploads,
    accept: 'image/*',
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>();
  const isPublished = weddingData?.weddingWebsite?.state === 'published';

  const closeRightRail = () => {
    if (pathname.includes('/preview')) {
      history.push(previewRoute);
    } else {
      history.push('/gs/dashboard');
    }
  };

  const openPreview = () => {
    const routeName = pageData?.page.routeName ? pageData?.page.routeName : '';
    history.push(previewPageRoute(routeName));
  };

  const deleteGallery = async () => {
    try {
      if (id) {
        await destroy({
          variables: {
            attributes: {
              pageId,
              id,
            },
          },
          update(cache) {
            deletePageItem({ cache, pageId, itemId: id });
          },
          refetchQueries: [{ query: pagesQuery }],
        });
      }
    } catch (error) {
      Honeybadger.notify(error, 'Error deleting Photo Gallery');
    }

    closeRightRail();
  };

  const onSortEnd = async ({ oldIndex, newIndex }: SortEnd): Promise<void> => {
    // Copy to a different variable so can reset on error.
    // It's possible photoItems gets updated before the promise finishes
    const existingItems = [...photoItems];
    const sorted = reorder(existingItems, oldIndex, newIndex);
    setPhotoItems(sorted);
    try {
      if (id) {
        await update({
          variables: {
            attributes: {
              pageId,
              photoItems: sorted.map(image => {
                return {
                  pageId,
                  id: image.id || null,
                  mediaApiId: image.mediaApiId || '',
                  height: image.height || 0,
                  width: image.width || 0,
                  rank: image.rank,
                };
              }),
              id,
            },
          },
          update: (_cache, { data }) => trackItem(data?.item),
        });
      }
    } catch (error) {
      Honeybadger.notify(error, 'Error Reordering');
      setPhotoItems(existingItems);
    }
  };

  let useSaveAndPreview = true;
  if (pathname.includes('preview')) {
    // If we are on preview, we don't want the action to be "Save & Preview",
    // just "Save & Close"
    useSaveAndPreview = false;
  }

  const primaryText = useSaveAndPreview ? 'Save & Preview' : isPublished ? 'Save & publish' : 'Save';

  return (
    <>
      {deleteConfirmOpen ? (
        <Confirmation
          data-testid="delete-confirm"
          info={<div>Are you sure you want to delete this photo gallery?</div>}
          onPrimaryAction={deleteGallery}
          onSecondaryAction={() => setDeleteConfirmOpen(false)}
          primaryText="Yes"
          question="Delete Photo Gallery"
          secondaryText="No"
        />
      ) : (
        <>
          <Header leftButtonOnClick={history.goBack} leftButtonText="Back" as="h1">{`${
            isNew ? 'Add a' : 'Edit'
          } Photo Gallery`}</Header>
          <Content>
            <>
              {isGalleryLoading ? (
                <div className={styles.loading}>
                  <Spinner />
                </div>
              ) : photoItems.length > 0 ? (
                <>
                  <PhotoGalleryGrid
                    addUploadsOnChange={addUploadsOnChange}
                    axis="xy"
                    helperClass="dragging"
                    onSortEnd={onSortEnd}
                    pageId={pageId}
                    photoGalleryId={id}
                    photoItems={photoItems}
                    useDragHandle
                  />
                  <div className={styles.removerContainer}>
                    <Remover onClick={() => setDeleteConfirmOpen(true)}>Delete Gallery</Remover>
                  </div>
                </>
              ) : (
                <div {...getRootProps()} className={styles.dropzone}>
                  <input {...getInputProps()} data-testid="photo-gallery-upload" />
                  <Icon className={styles.dropzoneIcon} name="download" size="lg" />
                  <span>
                    Click to Select Photos <br />
                    or Drag Them Here
                  </span>
                </div>
              )}
            </>
          </Content>
          <Footer>
            <TripleAction
              secondaryText="Save & Close"
              secondaryOnClick={closeRightRail}
              primaryText={useSaveAndPreview ? 'Save & Preview' : primaryText}
              primaryOnClick={openPreview}
            />
          </Footer>
        </>
      )}
    </>
  );
};

export default PhotoGalleryEdit;
